import { InfoOutlined, OpenInFull } from '@mui/icons-material';
import {
    Box,
    Button,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import { getCalculationResult } from '/@/store/carbon-calculator/selectors';
import co2Red from '../assets/CO2-Rojo.svg';
import co2Green from '../assets/CO2-Verde.svg';
import { round, type SeasonResult, seasonLabel } from '../utils';
import CarbonCharts from './charts';
import { sectionSx } from './styles';

function HeaderWithInfo({ label, info }: { label: string; info: string }) {
    return (
        <Stack direction='row' spacing={0.5} alignItems='center'>
            <span>{label}</span>
            <Tooltip title={info} arrow enterTouchDelay={0}>
                <InfoOutlined sx={{ fontSize: 16, cursor: 'pointer' }} />
            </Tooltip>
        </Stack>
    );
}

export function Co2Balance({ value }: { value: number }) {
    if (value === 0) return <>—</>;
    const positive = value > 0;
    return (
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' component='span'>
            <Box component='span' sx={{ color: positive ? 'success.main' : 'error.main', fontWeight: 500 }}>
                {positive ? `+${round(value, 3)}` : round(value, 3)}
            </Box>
            {/* "suelo" over a ground line, with the CO2 cloud's arrows crossing it. */}
            <Stack
                direction='row'
                spacing={1.25}
                alignItems='flex-end'
                component='span'
                sx={{ borderBottom: (t) => `1px solid ${t.palette.grey[400]}`, pr: 1 }}
            >
                <Typography component='span' variant='body2' color='text.secondary' lineHeight={1.2}>
                    {_('suelo')}
                </Typography>
                <Box
                    component='img'
                    src={positive ? co2Green : co2Red}
                    alt=''
                    sx={{ width: 35, height: 30, mb: '-6px' }}
                />
            </Stack>
        </Stack>
    );
}

function ResultsTable({ data }: { data: SeasonResult[] }) {
    const units = useUnitProvider();
    const areaUnit = units.getUnit('area', undefined);

    return (
        // Scrolls sideways on a phone instead of squeezing the long column headers.
        <TableContainer>
            <Table size='small' sx={{ minWidth: 480 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>{_('Campaña')}</TableCell>
                        <TableCell align='right'>
                            <HeaderWithInfo
                                label={_('Carbono orgánico en el suelo (SIM - Mg/{{unit}}-1)', { unit: areaUnit })}
                                info={_(
                                    'Indicador de calidad del suelo altamente relacionado con la Materia Orgánica. Simula las reservas o stock de C en el suelo.'
                                )}
                            />
                        </TableCell>
                        <TableCell align='right'>
                            <HeaderWithInfo
                                label={_('Balance CO2 por campaña (Mg/{{unit}}-1)', { unit: areaUnit })}
                                info={_(
                                    'CO2 secuestrado por las plantas, resultado de las diferencias entre el CO2 atmosférico absorbido durante el proceso de la fotosíntesis y el CO2 emitido durante la respiración, interaccionando simultáneamente con la composición del suelo.  1 tn de C Suelo equivale a 3,663 TN de CO2'
                                )}
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((season) => (
                        <TableRow key={season.season}>
                            <TableCell>
                                <Typography variant='body2' color='primary'>
                                    {seasonLabel(season.season)}
                                </Typography>
                            </TableCell>
                            <TableCell align='right'>{round(season.co.simulated, 2)}</TableCell>
                            <TableCell align='right'>
                                <Co2Balance value={season.co.fixed} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function ResultsContent({ data, expanded = false }: { data: SeasonResult[]; expanded?: boolean }) {
    if (data.length === 0) {
        return (
            <Typography variant='body2' color='primary.mui.inactive'>
                {_('Cargá al menos un cultivo con su rendimiento para ver los resultados.')}
            </Typography>
        );
    }
    return (
        <Stack spacing={3}>
            <ResultsTable data={data} />
            <CarbonCharts calculationResult={data} sideBySide={expanded} />
        </Stack>
    );
}

interface ResultsCardProps {
    isCalculating: boolean;
    hasError: boolean;
    onRetry: () => void;
}

export default function ResultsCard({ isCalculating, hasError, onRetry }: ResultsCardProps) {
    const data: SeasonResult[] = useSelector(getCalculationResult);
    const [expanded, setExpanded] = useState(false);

    return (
        <Stack spacing={2} sx={{ ...sectionSx, backgroundColor: 'background.paper', position: 'relative' }}>
            {isCalculating && (
                <LinearProgress color='secondary' sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />
            )}
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='h6' color='primary'>
                    {_('Resultados')}
                </Typography>
                {data.length > 0 && (
                    <Button color='secondary' size='small' startIcon={<OpenInFull />} onClick={() => setExpanded(true)}>
                        {_('Expandir')}
                    </Button>
                )}
            </Stack>
            {hasError && (
                <Alert severity='danger' actions={[{ kind: 'link', label: _('Reintentar'), onClick: onRetry }]}>
                    {_('No pudimos calcular los resultados.')}
                </Alert>
            )}
            <ResultsContent data={data} />

            <ModalV2
                open={expanded}
                onClose={() => setExpanded(false)}
                title={_('Resultados')}
                maxWidth='lg'
                fullScreenOnMobile
                bodyProps={{ alignItems: 'stretch', textAlign: 'left' }}
            >
                <ResultsContent data={data} expanded />
            </ModalV2>
        </Stack>
    );
}
